import { useEffect, useMemo, useRef } from 'react';
import { Project } from '../types';
import { projectsService, QuotaCheckResult } from '../services/supabase';
import { useInfiniteData } from './useInfiniteData';
import {
  useOptimisticCRUD,
  createOptimisticItem,
  OptimisticQuotaBlocked,
} from './useOptimisticCRUD';
import { useQuota } from '../context/QuotaContext';
import { PROJECT_UPDATED_EVENT } from '../utils/projectEvents';

interface UseInfiniteProjectsOptions {
  limit?: number;
  excludeArchived?: boolean;
  onlyArchived?: boolean;
  /** Optional: called when an update fails before rollback (e.g. show toast) */
  onUpdateFailed?: () => void;
}

interface ProjectCRUD {
  create: (data: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>) => void;
  update: (id: string, data: Project, options?: { previousItem?: Project }) => void;
  remove: (id: string) => void;
  bulkRemove: (ids: string[]) => void;
  bulkUpdate: (projects: Project[]) => void;
}

interface UseInfiniteProjectsReturn {
  projects: Project[];
  total?: number;
  loadingInitial: boolean;
  loadingMore: boolean;
  refreshing: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
  reset: () => void;
  crud: ProjectCRUD;
  addProject: (project: Project) => void;
}

export const useInfiniteProjects = (options: UseInfiniteProjectsOptions = {}): UseInfiniteProjectsReturn => {
  const { limit = 30, excludeArchived = false, onlyArchived = false, onUpdateFailed } = options;
  const { showQuotaLimit } = useQuota();
  const listActionsRef = useRef({
    removeItem: (_id: string) => {},
    replaceItem: (_tempId: string, _item: Project) => {},
  });

  const {
    items: projects,
    total,
    loadingInitial,
    loadingMore,
    refreshing,
    error,
    hasMore,
    loadMore,
    refresh,
    reset,
    addItem,
    updateItem,
    removeItem,
    replaceItem,
  } = useInfiniteData<Project>({
    limit,
    cacheKey: `projects:excludeArchived=${excludeArchived}:onlyArchived=${onlyArchived}`,
    service: {
      getCursorPage: async (l, cursor) => {
        const result = await projectsService.getCursorPage(l, cursor as any, { excludeArchived, onlyArchived });
        return {
          items: result.projects,
          total: result.total,
          nextCursor: result.nextCursor,
          hasMore: result.hasMore,
        };
      },
    },
  });

  listActionsRef.current = { removeItem, replaceItem };

  const optimisticCRUD = useOptimisticCRUD<
    Project,
    Omit<Project, 'id' | 'createdAt' | 'updatedAt'>,
    QuotaCheckResult
  >({
    onOptimisticAdd: addItem,
    onOptimisticUpdate: updateItem,
    onOptimisticRemove: removeItem,
    onOptimisticReplace: replaceItem,
    service: {
      create: async (data) => {
        const result = await projectsService.create(data) as any;
        if (result?.error === 'QUOTA_LIMIT') {
          return {
            __quotaBlocked: true,
            quotaInfo: result.quotaInfo as QuotaCheckResult,
          } satisfies OptimisticQuotaBlocked<QuotaCheckResult>;
        }
        return result && !result.error ? (result as Project) : null;
      },
      update: async (id, data) => {
        return await projectsService.update(id, data as Project);
      },
      delete: async (id) => {
        return await projectsService.delete(id);
      },
    },
    onQuotaBlocked: ({ tempId, data, quotaInfo }) => {
      showQuotaLimit(quotaInfo, {
        onDismiss: () => {
          listActionsRef.current.removeItem(tempId);
        },
        onSlotFreed: async () => {
          const created = await projectsService.create(data) as any;
          if (created && !created.error && created.id) {
            listActionsRef.current.replaceItem(tempId, created as Project);
            return;
          }
          listActionsRef.current.removeItem(tempId);
        },
      });
      return true;
    },
    onRollback: refresh,
    onUpdateFailed,
  });

  useEffect(() => {
    const onExternalUpdate = (event: Event) => {
      const project = (event as CustomEvent<Project>).detail;
      if (!project?.id) return;

      if (excludeArchived && project.status === 'ARCHIVED') {
        removeItem(project.id);
        return;
      }
      if (onlyArchived && project.status !== 'ARCHIVED') {
        removeItem(project.id);
        return;
      }
      if (onlyArchived && project.status === 'ARCHIVED') {
        addItem(project);
        return;
      }
      updateItem(project);
    };

    window.addEventListener(PROJECT_UPDATED_EVENT, onExternalUpdate);
    return () => window.removeEventListener(PROJECT_UPDATED_EVENT, onExternalUpdate);
  }, [excludeArchived, onlyArchived, addItem, updateItem, removeItem]);

  const crud: ProjectCRUD = useMemo(
    () => ({
      create: (data) => {
        // Garantir montant et statut pour l'affichage optimiste (évite rechargement pour voir)
        const safeData = {
          ...data,
          amount: typeof data.amount === 'number' ? data.amount : 0,
          status: data.status || 'BRIEF_RECEIVED',
        };
        const optimisticProject = createOptimisticItem<Project>(safeData);
        optimisticCRUD.create(data, optimisticProject);
      },
      update: (id, data, options) => optimisticCRUD.update(id, { ...data, updatedAt: new Date() }, options),
      remove: (id) => optimisticCRUD.remove(id),
      bulkRemove: (ids) => optimisticCRUD.bulkRemove(ids),
      bulkUpdate: (items) => {
        const updated = items.map((p) => ({ ...p, updatedAt: new Date() }));
        optimisticCRUD.bulkUpdate(updated);
      },
    }),
    [optimisticCRUD]
  );

  return {
    projects,
    total,
    loadingInitial,
    loadingMore,
    refreshing,
    error,
    hasMore,
    loadMore,
    refresh,
    reset,
    crud,
    addProject: addItem,
  };
};
